import { useEffect, useMemo } from 'react'

import { profileBelongsToIdentity } from '@/features/Auth/machine/identity'
import { useFirebaseUser } from '@/features/Auth/identity/useFirebaseUser'
import { useAppSelector } from '@/redux'
import { analytics } from '@/services/analytics/segment'
import { useProfile } from '@/services/api/session/useProfile'

/**
 * Who is signed in, as the rest of the app asks it.
 *
 * Distinct from `useAuth`, which is about a sign-in currently happening: this answers the
 * standing question, and every screen that gates on being logged in reads it. It resolves on
 * the uid alone — matching on the address as well was the client's copy of the backend lookup
 * EPIC-004 removed: the address is free in the shared Firebase pool whenever the row's
 * identity was never created there, so anyone who minted an account holding it would have
 * been read as that person. See EPIC-005.
 */
export function useAuthState() {
  const firebaseUser = useFirebaseUser()
  const profile = useProfile()

  const profileMatchesUser = useMemo(
    () => profileBelongsToIdentity(profile.data, firebaseUser?.uid),
    [profile.data, firebaseUser]
  )

  const loginResolved =
    firebaseUser === undefined ? false : !!firebaseUser?.uid ? profileMatchesUser || profile.isError : true

  const authFinished = useAppSelector(store => store.AppStatus.authFinished)
  const initialAuthResolved = useAppSelector(store => store.AppStatus.hasResolvedInitialAuth)

  return {
    isLoggedIn: profileMatchesUser,
    loginResolved,
    authFinished,
    initialAuthResolved
  }
}

export function useAutoLogin() {
  const firebaseUser = useFirebaseUser()
  const profile = useProfile({ enabled: true } as never)

  const profileMatchesUser = useMemo(
    () => profileBelongsToIdentity(profile.data, firebaseUser?.uid),
    [profile.data, firebaseUser]
  )

  // Keyed on the profile id as well as the login flag: without it, switching accounts
  // without an unmount leaves the flag true and the new user is never identified. The
  // client itself drops a repeat identify for the same user, so a flag that flips on a
  // profile refetch costs nothing.
  useEffect(() => {
    if (!!profile.data && profileMatchesUser) {
      analytics.identify({ user: profile.data, isLoggedIn: true })
      analytics.track('logged_in_pageview')
    }
  }, [profileMatchesUser, profile.data?.id])

  return {
    isLoggedIn: profileMatchesUser,
    profile: profile.data
  }
}
